import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { UniversalLibraryContextValue, ContentTypeConfig } from './types';
import { Image, Microscope, BookOpen, FileText } from 'lucide-react';
import { useUniversalStoragePreferences } from '@/hooks/universal-storage/useUniversalStoragePreferences';
import { createDefaultContext } from './DefaultContext';

// STATIC ADAPTERS - defined outside component to prevent recreation
const STATIC_ADAPTERS = {
  // 🚫 PUBLIC ADAPTERS REMOVED - No longer in use
  // image: {
  //   adapt: (img: any) => ({
  //     id: img.id,
  //     contentType: "image",
  //     title: img.description || "Untitled Image",
  //     description: img.original_prompt,
  //     createdAt: img.created_at,
  //     imageUrl: img.image_url,
  //     isFavorited: !!img.favorite_count && img.favorite_count > 0,
  //     icon: Image,
  //     originalItem: img,
  //   }),
  // },
  // research: {
  //   adapt: (research: any) => ({
  //     id: research.id,
  //     contentType: "research",
  //     title: research.title || "Untitled Research",
  //     description: research.content || research.original_prompt || "",
  //     createdAt: research.created_at,
  //     isFavorited: !!research.favorite_count && research.favorite_count > 0,
  //     icon: Microscope,
  //     originalItem: research,
  //   }),
  // },
  
  workbook: {
    adapt: (workbook: any) => ({
      id: workbook.id,
      contentType: "workbook",
      title: workbook.title || "Untitled Workbook",
      description: workbook.initial_request || "No description",
      createdAt: workbook.created_at,
      isFavorited: !!workbook.is_favorite,
      icon: BookOpen,
      originalItem: workbook,
    }),
  },

  private_image: {
    adapt: (img: any) => ({
      id: img.id,
      contentType: "private_image",
      title: img.alt_text || img.generation_prompt || "Private Image",
      description: img.generation_prompt || img.alt_text || "",
      createdAt: img.created_at,
      imageUrl: img.image_url,
      isFavorited: false, // Private images don't have favorites
      icon: Image,
      originalItem: img,
    }),
  },
  private_research: {
    adapt: (research: any) => ({
      id: research.id,
      contentType: "private_research",
      title: research.title || "Untitled Private Research",
      description: research.content || research.original_prompt || "",
      createdAt: research.created_at,
      isFavorited: false, // Private research doesn't have favorites
      icon: Microscope,
      originalItem: research,
    }),
  },
  private_page: {
    adapt: (page: any) => ({
      id: page.id,
      contentType: "private_page",
      title: page.title || "Untitled Document",
      description: page.content || page.initial_request || "",
      createdAt: page.created_at,
      isFavorited: false, // Private pages don't have favorites
      icon: FileText,
      originalItem: page,
    }),
  },
  // 🎯 NEW CONTENT TYPE ADAPTERS CAN BE ADDED HERE
  // Example:
  // new_type: {
  //   adapt: (item: any) => ({
  //     id: item.id,
  //     contentType: "new_type",
  //     title: item.title || "Untitled",
  //     description: item.description,
  //     createdAt: item.created_at,
  //     isFavorited: !!item.is_favorite,
  //     icon: NewIcon,
  //     originalItem: item,
  //   }),
  // },
};

// STATIC CONTENT TYPES - stable registration
const STATIC_CONTENT_TYPES: Record<string, ContentTypeConfig> = {
  // 🚫 PUBLIC CONTENT TYPES REMOVED - No longer in use
  // image: {
  //   type: "image",
  //   name: "Images",
  //   singularName: "Image",
  //   icon: Image,
  //   adapter: STATIC_ADAPTERS.image,
  //   useDataHook: null,
  //   actionType: "lightbox",
  // },
  // research: {
  //   type: "research",
  //   name: "Research",
  //   singularName: "Research",
  //   icon: Microscope,
  //   adapter: STATIC_ADAPTERS.research,
  //   useDataHook: null,
  //   actionType: "lightbox",
  // },
  
  workbook: {
    type: "workbook",
    name: "Workbooks",
    singularName: "Workbook",
    icon: BookOpen,
    adapter: STATIC_ADAPTERS.workbook,
    useDataHook: null,
    actionType: "navigate",
    getRoute: (item: any) => `/content-generator/workbook/${item.id}`,
  },

  // ✅ UPDATED: Private content types with clean names
  private_image: {
    type: "private_image",
    name: "Images",
    singularName: "Image", // Blue theme
    icon: Image,
    adapter: STATIC_ADAPTERS.private_image,
    useDataHook: null,
    actionType: "lightbox",
  },
  private_research: {
    type: "private_research",
    name: "Research", 
    singularName: "Research", // Green theme
    icon: Microscope,
    adapter: STATIC_ADAPTERS.private_research,
    useDataHook: null,
    actionType: "lightbox",
  },
  private_page: {
    type: "private_page",
    name: "Documents",
    singularName: "Document", // Purple theme
    icon: FileText,
    adapter: STATIC_ADAPTERS.private_page,
    useDataHook: null,
    actionType: "lightbox",
  },
  // 🎯 NEW CONTENT TYPE CONFIGS CAN BE ADDED HERE
  // Example:
  // new_type: {
  //   type: "new_type",
  //   name: "New Content",
  //   singularName: "New Item",
  //   icon: NewIcon,
  //   adapter: STATIC_ADAPTERS.new_type,
  //   useDataHook: null,
  //   actionType: "navigate",
  //   getRoute: (item: any) => `/new-type/edit/${item.id}`,
  // },
};

const UniversalLibraryContext = createContext<UniversalLibraryContextValue | undefined>(undefined);

export const UniversalLibraryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getStoredPreferences, savePreferences } = useUniversalStoragePreferences();
  
  // Initialize with stable content types and stored preferences
  const [state, setState] = useState(() => {
    const prefs = getStoredPreferences();
    return {
      selectedContentType: prefs.selectedContentType,
      searchQuery: '',
      selectedCategory: prefs.selectedCategory,
    };
  });

  // Stable content types - no dynamic registration needed
  const [contentTypes] = useState(STATIC_CONTENT_TYPES);

  // Refetch mechanism using useRef for stability
  const refetchCallbacksRef = useRef<Set<() => void>>(new Set());

  const onRefetchNeeded = useCallback((callback: () => void) => {
    console.log('[UniversalLibrary] Registering refetch callback');
    
    refetchCallbacksRef.current.add(callback);
    console.log('[UniversalLibrary] Added callback. New callback count:', refetchCallbacksRef.current.size);
    
    return () => {
      console.log('[UniversalLibrary] Unregistering refetch callback');
      refetchCallbacksRef.current.delete(callback);
      console.log('[UniversalLibrary] Removed callback. New callback count:', refetchCallbacksRef.current.size);
    };
  }, []); // Stable callback with no dependencies

  const triggerRefetch = useCallback(() => {
    const callbacks = refetchCallbacksRef.current;
    console.log('[UniversalLibrary] Triggering refetch for', callbacks.size, 'callbacks');
    let index = 0;
    callbacks.forEach(callback => {
      try {
        index++;
        console.log(`[UniversalLibrary] Executing callback ${index}/${callbacks.size}`);
        callback();
      } catch (error) {
        console.error('[UniversalLibrary] Error in refetch callback:', error);
      }
    });
  }, []); // Stable callback with no dependencies

  const registerContentType = useCallback((config: ContentTypeConfig) => {
    console.warn('[UniversalLibrary] Dynamic content type registration is deprecated. Use static configuration.');
  }, []);

  const setSelectedContentType = useCallback((type: string) => {
    setState(prev => ({ ...prev, selectedContentType: type }));
    savePreferences({ selectedContentType: type });
  }, [savePreferences]);

  const setSearchQuery = useCallback((query: string) => {
    setState(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const setSelectedCategory = useCallback((category: string | null) => {
    setState(prev => ({ ...prev, selectedCategory: category }));
    savePreferences({ selectedCategory: category });
  }, [savePreferences]);

  // Stable context value
  const value = useMemo(() => ({
    ...state,
    contentTypes,
    registerContentType,
    setSelectedContentType,
    setSearchQuery,
    setSelectedCategory,
    onRefetchNeeded,
    triggerRefetch,
  }), [
    state,
    contentTypes,
    registerContentType,
    setSelectedContentType,
    setSearchQuery,
    setSelectedCategory,
    onRefetchNeeded,
    triggerRefetch,
  ]);

  return (
    <UniversalLibraryContext.Provider value={value}>
      {children}
    </UniversalLibraryContext.Provider>
  );
};

export const useUniversalLibrary = () => {
  const context = useContext(UniversalLibraryContext);
  if (context === undefined) {
    console.warn('[UniversalLibrary] useUniversalLibrary called outside provider context, using fallback');
    return createDefaultContext();
  }
  return context;
};
